/**
 * Phase 6 (Feb 2026) — Post-login landing router.
 *
 * Single source of truth for the "where do we send the user after
 * they hit /app?" decision. Rules, in order:
 *
 *   1. Operators / platform_owners → /app/operator
 *   2. Freshly registered tenants  → /app/onboarding (one-shot)
 *      Detected by:
 *        - progress_pct === 0
 *        - !completed_at
 *        - !dismissed_banner
 *        - !initial_redirect_shown
 *      On trigger we POST /onboarding/mark-initial-redirect-shown
 *      AFTER navigating so the flag reflects an actual redirect. The
 *      OnboardingBanner on /app/home is a *display-only* safety net
 *      and MUST NOT set the flag on its own — otherwise the wizard
 *      trigger flips to true on a plain home visit and never fires
 *      the automatic redirect again for that tenant.
 *   3. Everyone else → /app/home
 *
 * Legacy tenants that landed on /app/home BEFORE the Phase 6 rework
 * shipped may have `initial_redirect_shown=true` even though they
 * never actually walked through the wizard. The `Onboarding` UI
 * itself is still reachable via sidebar + the /app/home banner
 * ("Setup jetzt starten"), so nothing is lost — the automatic
 * redirect is one-shot by design.
 */
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, auth } from "../../lib/api";

export default function PostLoginLanding() {
  const nav = useNavigate();
  useEffect(() => {
    let cancelled = false;
    async function decide() {
      const u = auth.getUser();
      if (!u) { nav("/login", { replace: true }); return; }
      if (u.role === "platform_owner" || u.role === "operator") {
        nav("/operator/dashboard", { replace: true });
        return;
      }
      let s = null;
      try {
        const r = await api.get("/onboarding");
        s = r?.data || null;
      } catch (_e) { s = null; }
      if (cancelled) return;
      // Phase 6 fix (Feb 2026):
      //   * When the API succeeds but the response is empty (missing
      //     progress_pct + completed_at fields), treat the tenant as
      //     fresh and send them to the wizard. `_load_or_init` on the
      //     backend always creates the state doc on first GET, so an
      //     empty body here means "no data ever" → wizard is the safe
      //     default, not /app/home.
      //   * When the API fails outright (network error, 500), fall
      //     through to /app/home so the app remains usable.
      const noState = s === null;
      const stateExists = s
        && ("progress_pct" in s || "completed_at" in s);
      const fresh = stateExists && (
        (s.progress_pct ?? 0) === 0
        && !s.completed_at
        && !s.dismissed_banner
        && !s.initial_redirect_shown
      );
      if (noState) {
        // Backend unreachable — keep the user on a safe surface.
        nav("/app/home", { replace: true });
        return;
      }
      if (fresh || !stateExists) {
        // Navigate FIRST so the user reaches the wizard even if the
        // POST fails, then mark the flag (fire-and-forget). This
        // ordering guarantees "flag only flips after a real redirect".
        nav("/app/onboarding", { replace: true });
        try { await api.post("/onboarding/mark-initial-redirect-shown"); }
        catch (_e) { /* silent — worst case: wizard fires once more */ }
        return;
      }
      nav("/app/home", { replace: true });
    }
    decide();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div
      data-testid="post-login-landing"
      className="min-h-[40vh] grid place-items-center text-sm text-muted-foreground"
    >
      Lade Arbeitsbereich…
    </div>
  );
}
